import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ModalController, Platform } from '@ionic/angular';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { BASE_URL_PATH, STONE_CREEK_LEARN_MORE_URL } from 'src/app/core/constants';
import {Merchant, Restaurant} from 'src/app/core/models/restaurant.model';
import { RouteService } from 'src/app/core/services/route.service';
import { StorageService } from 'src/app/core/services/storage.service';
import { UtilService } from 'src/app/core/services/util.service';
import { CartStore } from 'src/app/core/stores/cart.store';
import { MerchantStore } from 'src/app/core/stores/merchant.store';
import { RestaurantStore } from 'src/app/core/stores/restaurant.store';
import { LocationListComponent } from 'src/app/shared/components/location-list/location-list.component';
import { InAppBrowser } from '@ionic-native/in-app-browser/ngx';
import { OrderApiService } from 'src/app/core/services/order.api.service';
import { Geolocation } from '@ionic-native/geolocation/ngx';

@Component({
  selector: 'app-home-screen',
  templateUrl: './home-screen.component.html',
  styleUrls: ['./home-screen.component.scss'],
})
export class HomeScreenComponent implements OnInit {
  private destroyed$ = new Subject();
  public merchantLogoUrl: string;
  public merchantBannerUrl: string;
  public restaurantsInGroup: Restaurant[];
  public chosenRestaurant: Restaurant;
  public currentRestaurantId: string = null;
  public merchantId: string = null;

  constructor(
    public merchantStore: MerchantStore,
    private routeService: RouteService,
    private modalController: ModalController,
    private restaurantStore: RestaurantStore,
    private platform: Platform,
    private router: Router,
    public cartStore: CartStore,
    private storageService: StorageService,
    private utilService: UtilService,
    private iab: InAppBrowser,
    private orderApiService: OrderApiService,
    private geolocation: Geolocation
  ) {}

  public get isMobile(): boolean {
    return this.platform.is('mobile') || this.platform.width() < 600;
  }

  public get activeLocationBanner(): string {
    return this.restaurantsInGroup && this.restaurantsInGroup[this.restaurantStore.activeLocationIndex].locationBanner
      ? this.restaurantsInGroup[this.restaurantStore.activeLocationIndex].locationBanner
      : './assets/images/back.jpg';
  }

  public get activeLocationName(): string {
    return this.restaurantsInGroup && this.restaurantsInGroup[this.restaurantStore.activeLocationIndex].name
      ? this.restaurantsInGroup[this.restaurantStore.activeLocationIndex].name
      : '';
  }

  public get cartStoreItems(): boolean {
    return this.cartStore.items.length > 0;
  }

  ionViewDidEnter(): void {
    this.getLastRestaurantFromLocal();
  }

  async ngOnInit() {
    if (!this.restaurantsInGroup) this.utilService.activeLoader('Loading restaurants...');
    this.merchantId = this.routeService.getMerchantId();
    this.merchantStore.bootstrapRestaurantData(this.merchantId);
    this.restaurantStore.checkRouteContainsDiscountOption();
    this.getLocationPermission();
    this.merchantStore.merchantData$.pipe(filter(Boolean)).subscribe((merchant: Merchant) => {
      this.merchantLogoUrl = merchant?.merchantLogo;
      this.merchantBannerUrl = merchant?.merchantBanner;
    });

    this.restaurantStore.restaurants$.pipe(takeUntil(this.destroyed$), filter(Boolean)).subscribe((restaurants: Restaurant[]) => {
      if (restaurants.length !== 0) {
        this.restaurantsInGroup = restaurants;
        this.getLastRestaurantFromLocal();
        this.utilService.dismissActiveLoader();
      }
    });
  }

  public async chooseLocationMobile() : Promise<void> {
    if (this.restaurantsInGroup && this.restaurantsInGroup.length > 1) {
      const modal = await this.modalController.create({
        component: LocationListComponent,
        componentProps: {
          locationList: this.restaurantsInGroup,
          chosenLocationIndex: this.restaurantStore.activeLocationIndex,
        },
      });

      modal.onDidDismiss().then(async (data) => {
        const result = data['data'] ? data['data'].locationIndex : null;
        if (result != null) {
          this.restaurantStore.activeLocationIndex = data['data'].locationIndex;
          this.chosenRestaurant = this.restaurantsInGroup[this.restaurantStore.activeLocationIndex];
          if (this.currentRestaurantId !== this.chosenRestaurant.locationId) {
            this.cartStore.deleteItemsFromCartByLocationId(this.currentRestaurantId);
            this.orderApiService.removeLocationFromOrderMap(this.currentRestaurantId);
          }
          this.currentRestaurantId = this.chosenRestaurant.locationId;
          this.storageService.set('currentRestaurantId', this.currentRestaurantId);
        }
      });
      await modal.present();
    }
  }

  public navigateToRestaurant() : Promise<boolean> {
    if (this.currentRestaurantId) {
      this.storageService.set('currentRestaurantId', this.currentRestaurantId);
      const baseUrl = this.routeService.getBaseUrl();
      return this.router.navigate([baseUrl, 'locations', this.currentRestaurantId]);
    }
    return this.router.navigate([BASE_URL_PATH, this.merchantId]);
  }

  public async getLastRestaurantFromLocal(): Promise<void> {
    try {
      this.currentRestaurantId = await this.storageService.get('currentRestaurantId');
      if (this.currentRestaurantId && this.restaurantsInGroup) {
        const chosenRestaurant = this.restaurantsInGroup.filter((restaurant, index) => {
          if (restaurant.locationId === this.currentRestaurantId) {
            this.restaurantStore.activeLocationIndex = index;
            return restaurant;
          }
        });
        this.chosenRestaurant = chosenRestaurant ? chosenRestaurant[0] : null;
      } else {
        this.chosenRestaurant = this.restaurantsInGroup ? this.restaurantsInGroup[0] : null;
        this.currentRestaurantId = this.chosenRestaurant ? this.chosenRestaurant.locationId : null;
      }
    } catch (err) {
      this.chosenRestaurant = this.restaurantsInGroup ? this.restaurantsInGroup[0] : null;
      this.currentRestaurantId = this.chosenRestaurant ? this.chosenRestaurant.locationId : null;
    }
  }

  public async getUserCoordinates(): Promise<boolean> {
    const coordinates = { latitude: null, longitude: null };
    try {
      const response = await this.geolocation.getCurrentPosition();
      if (response && response.coords && response.coords.latitude && response.coords.longitude) {
        coordinates.latitude = response.coords.latitude;
        coordinates.longitude = response.coords.longitude;
        this.storageService.set('coordinates', coordinates);
        return true;
      }
      this.storageService.remove('coordinates');
      return false;
    } catch (err) {
      this.storageService.remove('coordinates');
      return false;
    }
  }

  public navigateToCheckout(): void {
    this.utilService.navigateToCheckout();
  }

  public navigateToStoneCreekSite(): void {
    this.utilService.openInAppBrowser(STONE_CREEK_LEARN_MORE_URL);
  }

  public navigateToRewards(): void {
    this.utilService.navigateToRewards();
  }

  public async getLocationPermission(): Promise<void> {
    const authState = await this.utilService.getCurrentAuthState();
    if (authState) {
      await this.getUserCoordinates();
    }
  }
}
